/**
 * Team Grid Modal
 * Handles opening/closing of team member bio modals with GSAP animations
 */

import { gsap } from 'gsap';

document.addEventListener('DOMContentLoaded', function() {
  const teamCards = document.querySelectorAll('.team-card');
  const modal = document.getElementById('team-modal');
  
  if (!teamCards.length || !modal) return;

  // Modal elements
  const modalOverlay = modal.querySelector('.team-modal-overlay');
  const modalContainer = modal.querySelector('.team-modal-container');
  const modalClose = modal.querySelector('.team-modal-close');
  const modalBack = modal.querySelector('.team-modal-back');
  const modalImage = document.getElementById('team-modal-image');
  const modalName = document.getElementById('team-modal-name');
  const modalTitle = document.getElementById('team-modal-title');
  const modalBio = document.getElementById('team-modal-bio');
  const modalSocial = document.getElementById('team-modal-social');
  
  // Social links
  const phoneLink = modalSocial.querySelector('.team-social-phone');
  const emailLink = modalSocial.querySelector('.team-social-email');
  const linkedinLink = modalSocial.querySelector('.team-social-linkedin');

  /**
   * Open modal with GSAP animation
   */
  function openModal(memberData) {
    // Populate modal content
    modalImage.style.backgroundImage = `url('${memberData.modalImage}')`;
    modalImage.style.backgroundPosition = `${memberData.modalFocalX}% ${memberData.modalFocalY}%`;
    modalName.textContent = memberData.name;
    modalTitle.textContent = memberData.title;
    modalBio.innerHTML = memberData.bio;
    
    // Handle social links
    if (memberData.phone) {
      phoneLink.href = `tel:${memberData.phone}`;
      phoneLink.style.display = 'flex';
    } else {
      phoneLink.style.display = 'none';
    }
    
    if (memberData.email) {
      emailLink.href = `mailto:${memberData.email}`;
      emailLink.style.display = 'flex';
    } else {
      emailLink.style.display = 'none';
    }
    
    if (memberData.linkedin) {
      linkedinLink.href = memberData.linkedin;
      linkedinLink.style.display = 'flex';
    } else {
      linkedinLink.style.display = 'none';
    }
    
    // Show modal
    modal.classList.add('is-active');
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    
    // Reset scroll position
    modal.querySelector('.team-modal-details').scrollTop = 0;
    
    // GSAP Animation Timeline
    const tl = gsap.timeline();
    
    // Set initial states
    gsap.set(modal, { opacity: 0 });
    gsap.set(modalContainer, { scale: 0.9, opacity: 0 });
    gsap.set(modalImage, { scale: 1.2, opacity: 0 });
    gsap.set([modalName, modalTitle, modalBio, modalSocial, modalBack], { opacity: 0, y: 20 });
    
    // Animate in - Image first, then content
    tl.to(modal, {
      opacity: 1,
      duration: 0.2,
      ease: 'power2.out'
    })
    .to(modalContainer, {
      scale: 1,
      opacity: 1,
      duration: 0.4,
      ease: 'power2.out'
    }, '-=0.1')
    .to(modalImage, {
      scale: 1,
      opacity: 1,
      duration: 0.6,
      ease: 'power3.out'
    }, '-=0.2')
    .to(modalBack, {
      opacity: 1,
      y: 0,
      duration: 0.3,
      ease: 'power2.out'
    }, '-=0.3')
    .to([modalName, modalTitle], {
      opacity: 1,
      y: 0,
      duration: 0.4,
      stagger: 0.1,
      ease: 'power2.out'
    }, '-=0.2')
    .to(modalBio, {
      opacity: 1,
      y: 0,
      duration: 0.4,
      ease: 'power2.out'
    }, '-=0.2')
    .to(modalSocial, {
      opacity: 1,
      y: 0,
      duration: 0.3,
      ease: 'power2.out'
    }, '-=0.1');
  }

  /**
   * Close modal with GSAP animation
   */
  function closeModal() {
    const tl = gsap.timeline({
      onComplete: () => {
        modal.classList.remove('is-active');
        modal.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
      }
    });
    
    // Animate out
    tl.to([modalSocial, modalBio], {
      opacity: 0,
      y: 20,
      duration: 0.2,
      stagger: 0.05,
      ease: 'power2.in'
    })
    .to([modalTitle, modalName, modalBack], {
      opacity: 0,
      y: 20,
      duration: 0.2,
      stagger: 0.05,
      ease: 'power2.in'
    }, '-=0.1')
    .to(modalContainer, {
      scale: 0.9,
      y: 30,
      duration: 0.3,
      ease: 'back.in(1.2)'
    }, '-=0.1')
    .to(modal, {
      opacity: 0,
      duration: 0.2,
      ease: 'power2.in'
    }, '-=0.2');
  }

  /**
   * Event Listeners
   */

  // Open modal on card click
  teamCards.forEach(card => {
    card.addEventListener('click', function() {
      const memberData = {
        name: this.dataset.memberName,
        title: this.dataset.memberTitle,
        bio: this.dataset.memberBio,
        phone: this.dataset.memberPhone,
        email: this.dataset.memberEmail,
        linkedin: this.dataset.memberLinkedin,
        modalImage: this.dataset.modalImage,
        modalFocalX: this.dataset.modalFocalX,
        modalFocalY: this.dataset.modalFocalY
      };
      
      openModal(memberData);
    });

    // Keyboard accessibility
    card.setAttribute('role', 'button');
    card.setAttribute('tabindex', '0');
    card.addEventListener('keydown', function(e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.click();
      }
    });
  });

  // Close modal on close button
  modalClose.addEventListener('click', closeModal);

  // Close modal on back button
  modalBack.addEventListener('click', closeModal);

  // Close modal on overlay click
  modalOverlay.addEventListener('click', closeModal);

  // Close modal on Escape key
  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape' && modal.classList.contains('is-active')) {
      closeModal();
    }
  });

  // Prevent clicks inside modal container from closing
  modalContainer.addEventListener('click', function(e) {
    e.stopPropagation();
  });
});
